// ============================================
// CORPORATE SERVICES UAE - All-in-One Dashboard
// Connected to Firebase/Backend
// ============================================
// FIX APPLIED: Removed duplicate /api prefix from all API calls
// The apiUrl prop already includes /api, so we only need /corporate/...

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import { 
  Upload, FileText, Check, Clock, AlertCircle, ChevronRight, 
  Building2, Calendar, CreditCard, Receipt, Shield, Bell,
  ChevronDown, ChevronUp, ExternalLink, Plus, X, Trash2, RefreshCw, ClipboardList
} from 'lucide-react';

interface CorporateServicesProps {
  currentInvestor: any;
  apiUrl: string;
}

interface Company {
  id: string;
  companyName: string;
  licenseNumber: string;
  licenseExpiry: string;
  licenseType: string;
  freeZone: string;
  formationProgress: number;
  ctRegistrationRequired: boolean;
  ctRegistrationDeadline: string | null;
  ctFilingDue: string | null;
  vatRegistered: boolean;
  currentRevenue: number;
}

interface CorporateDocument {
  id: string;
  documentType: string;
  fileName: string;
  fileUrl: string;
  status: 'pending' | 'verified' | 'rejected';
  uploadedAt: any;
}

interface ReceiptItem {
  id: string;
  description: string;
  amount: number;
  category: string;
  date: string;
  fileUrl?: string;
}

// Timeline steps for company formation
const FORMATION_STEPS = [
  { id: 'trade_license', key: 'tradeLicense', required: true },
  { id: 'moa', key: 'moa', required: true },
  { id: 'lease_agreement', key: 'unitLeaseAgreement', required: true },
  { id: 'establishment_card', key: 'establishmentCard', required: true },
  { id: 'bank_statement', key: 'bankAccountOpening', required: false },
  { id: 'visa', key: 'visaSetup', required: false },
];

// Single Calendly link for all corporate bookings
const CALENDLY_LINK = 'https://calendly.com/j-kijasko-investmenter/internationale-steuerberatung-investmenter-app';

const VAT_THRESHOLD = 375000;

const CorporateServicesView: React.FC<CorporateServicesProps> = ({ currentInvestor, apiUrl }) => {
  const { t } = useLanguage();
  
  // State management
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['formation', 'compliance']));
  
  // Data state
  const [company, setCompany] = useState<Company | null>(null);
  const [documents, setDocuments] = useState<CorporateDocument[]>([]);
  const [receipts, setReceipts] = useState<ReceiptItem[]>([]);
  const [vatFilings, setVatFilings] = useState<any[]>([]);
  
  // Modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [showCompanySetup, setShowCompanySetup] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  
  // Form state for new company
  const [newCompany, setNewCompany] = useState({
    companyName: '',
    licenseNumber: '',
    licenseExpiry: '',
    establishmentDate: '',
    freeZone: 'DMCC'
  });

  // Form state for new receipt
  const [newReceipt, setNewReceipt] = useState({
    description: '',
    amount: '',
    category: 'operations',
    date: new Date().toISOString().split('T')[0]
  });

  // ============================================
  // DATA FETCHING
  // ============================================
  
  const fetchCorporateData = useCallback(async () => {
    if (!currentInvestor?.id) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    // URL encode the investorId in case it contains special characters (like @ in email)
    const encodedInvestorId = encodeURIComponent(currentInvestor.id);
    
    try {
      // FIX: Changed from /api/corporate to /corporate (apiUrl already has /api)
      const response = await fetch(`${apiUrl}/corporate/company/${encodedInvestorId}`);
      
      
      if (!response.ok) {
        throw new Error('Failed to fetch corporate data');
      }
      
      const data = await response.json();
      
      setCompany(data.company);
      setDocuments(data.documents || []);
      setReceipts(data.receipts || []);
      setVatFilings(data.vatFilings || []);
      
      // Show setup modal if no company exists
      if (!data.company) {
        setShowCompanySetup(true);
      }
      
    } catch (err: any) {
      console.error('❌ Error fetching corporate data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentInvestor?.id, apiUrl]);

  useEffect(() => {
    fetchCorporateData();
  }, [fetchCorporateData]);

  // ============================================
  // COMPANY MANAGEMENT
  // ============================================
  
  const handleCreateCompany = async () => {
    if (!newCompany.companyName || !newCompany.licenseNumber) {
      alert(t('pleaseEnterCompanyDetails'));
      return;
    }
    
    setIsLoading(true); // Show loading while creating
    
    try {
      // FIX: Changed from /api/corporate to /corporate (apiUrl already has /api)
      const response = await fetch(`${apiUrl}/corporate/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          investorId: currentInvestor.id,
          ...newCompany
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create company');
      }
      
      const result = await response.json();
      
      // Close the setup modal
      setShowCompanySetup(false);
      
      // Small delay to ensure Firestore has the data
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // URL encode the investorId
      const encodedInvestorId = encodeURIComponent(currentInvestor.id);
      
      // Fetch the updated data with retry
      let retries = 3;
      while (retries > 0) {
        try {
          const dataResponse = await fetch(`${apiUrl}/corporate/company/${encodedInvestorId}`);
          const data = await dataResponse.json();
          
          if (data.company) {
            setCompany(data.company);
            setDocuments(data.documents || []);
            setReceipts(data.receipts || []);
            setVatFilings(data.vatFilings || []);
            setIsLoading(false);
            return;
          }
        } catch (e) {
        }
        
        retries--;
        if (retries > 0) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      // If retries exhausted, set company from the create response data
      // This is a fallback - create a minimal company object
      setCompany({
        id: result.companyId,
        companyName: newCompany.companyName,
        licenseNumber: newCompany.licenseNumber,
        licenseExpiry: newCompany.licenseExpiry,
        licenseType: 'FZCO',
        freeZone: newCompany.freeZone,
        formationProgress: 0,
        ctRegistrationRequired: true,
        ctRegistrationDeadline: null,
        ctFilingDue: null,
        vatRegistered: false,
        currentRevenue: 0
      });
      setIsLoading(false);
      
    } catch (err: any) {
      console.error('❌ Error creating company:', err);
      alert('Error: ' + err.message);
      setIsLoading(false);
    }
  };

  // ============================================
  // DOCUMENT MANAGEMENT
  // ============================================
  
  const handleUpload = (type: string) => {
    setUploadType(type);
    setShowUploadModal(true);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result as string;
        
        // FIX: Changed from /api/corporate to /corporate (apiUrl already has /api)
        const response = await fetch(`${apiUrl}/corporate/documents`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            companyId: company?.id,
            investorId: currentInvestor.id,
            documentType: uploadType,
            fileName: file.name,
            fileData: base64,
            mimeType: file.type
            // No autoVerify - documents will be PENDING until admin verifies
          })
        });
        
        if (!response.ok) throw new Error('Upload failed');
        
        setShowUploadModal(false);
        setIsUploading(false);
        fetchCorporateData();
      };
      
      reader.readAsDataURL(file);
      
    } catch (err: any) {
      setIsUploading(false);
      alert('Upload error: ' + err.message);
    }
  };

  // FIX: Changed confirm() to window.confirm() to fix ESLint no-restricted-globals error
  const handleDeleteDocument = async (docId: string) => {
    if (!window.confirm(t('confirmDeleteDocument'))) return;
    
    try {
      // FIX: Changed from /api/corporate to /corporate (apiUrl already has /api)
      await fetch(`${apiUrl}/corporate/documents/${docId}`, {
        method: 'DELETE'
      });
      fetchCorporateData();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  // ============================================
  // RECEIPT MANAGEMENT
  // ============================================
  
  const handleAddReceipt = async () => {
    if (!newReceipt.description || !newReceipt.amount) {
      alert(t('pleaseEnterReceiptDetails'));
      return;
    }
    
    try {
      // FIX: Changed from /api/corporate to /corporate (apiUrl already has /api)
      const response = await fetch(`${apiUrl}/corporate/receipts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: company?.id,
          investorId: currentInvestor.id,
          ...newReceipt,
          amount: parseFloat(newReceipt.amount)
        })
      });
      
      if (!response.ok) throw new Error('Failed to add receipt');
      
      setShowReceiptModal(false);
      setNewReceipt({ description: '', amount: '', category: 'operations', date: new Date().toISOString().split('T')[0] });
      fetchCorporateData();
      
    } catch (err: any) {
      alert('Error: ' + err.message);
    }
  };

  // FIX: Changed confirm() to window.confirm() to fix ESLint no-restricted-globals error
  const handleDeleteReceipt = async (receiptId: string) => {
    if (!window.confirm(t('confirmDeleteReceipt'))) return;
    
    try {
      // FIX: Changed from /api/corporate to /corporate (apiUrl already has /api)
      await fetch(`${apiUrl}/corporate/receipts/${receiptId}`, {
        method: 'DELETE'
      });
      fetchCorporateData();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  // ============================================
  // CALCULATIONS
  // ============================================
  
  const daysUntilExpiry = useMemo(() => {
    if (!company?.licenseExpiry) return 999;
    const expiry = new Date(company.licenseExpiry);
    const today = new Date();
    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }, [company?.licenseExpiry]);

  const vatProgress = useMemo(() => {
    const revenue = company?.currentRevenue || 0;
    return Math.min((revenue / VAT_THRESHOLD) * 100, 100);
  }, [company?.currentRevenue]);

  const totalExpenses = useMemo(() => {
    return receipts.reduce((sum, r) => sum + (r.amount || 0), 0);
  }, [receipts]);

  const getDocumentByType = (type: string) => {
    return documents.find(doc => doc.documentType === type);
  };

  const verifiedDocsCount = useMemo(() => {
    return documents.filter(d => d.status === 'verified').length;
  }, [documents]);

  // ============================================
  // HELPERS
  // ============================================
  
  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const formatAED = (amount: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const openCalendly = () => {
    window.open(CALENDLY_LINK, '_blank');
  };

  // ============================================
  // STYLES
  // ============================================
  
  const styles = {
    container: {
      padding: '16px',
      paddingBottom: '100px',
      fontFamily: "'Montserrat', -apple-system, sans-serif",
      background: '#f5f5f5',
      minHeight: '100vh'
    } as React.CSSProperties,
    header: {
      marginBottom: '20px',
      paddingBottom: '15px',
      borderBottom: '3px solid #D4AF37'
    } as React.CSSProperties,
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#1a1a2e',
      margin: '0 0 5px 0',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    } as React.CSSProperties,
    subtitle: {
      color: '#666',
      fontSize: '0.85rem',
      margin: 0
    } as React.CSSProperties,
    overviewCard: {
      background: 'linear-gradient(135deg, #1a1a2e 0%, #2d2d4a 100%)',
      borderRadius: '16px',
      padding: '20px',
      marginBottom: '16px',
      color: '#fff'
    } as React.CSSProperties,
    companyName: {
      fontSize: '1.2rem',
      fontWeight: '700',
      color: '#D4AF37',
      margin: '0 0 4px 0'
    } as React.CSSProperties,
    licenseNumber: {
      fontSize: '0.8rem',
      color: '#aaa',
      margin: '0 0 16px 0'
    } as React.CSSProperties,
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px'
    } as React.CSSProperties,
    statItem: {
      background: 'rgba(255,255,255,0.1)',
      borderRadius: '10px',
      padding: '12px'
    } as React.CSSProperties,
    statLabel: {
      fontSize: '0.65rem',
      color: '#aaa',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      marginBottom: '4px'
    } as React.CSSProperties,
    statValue: {
      fontSize: '1.1rem',
      fontWeight: '700',
      color: '#fff'
    } as React.CSSProperties,
    section: {
      background: '#fff',
      borderRadius: '12px',
      marginBottom: '12px',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
    } as React.CSSProperties,
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px',
      cursor: 'pointer',
      borderBottom: '1px solid #f0f0f0'
    } as React.CSSProperties,
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '0.95rem',
      fontWeight: '600',
      color: '#1a1a2e',
      margin: 0
    } as React.CSSProperties,
    sectionIcon: {
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    } as React.CSSProperties,
    sectionContent: {
      padding: '16px'
    } as React.CSSProperties,
    timeline: {
      position: 'relative' as const,
      paddingLeft: '24px'
    } as React.CSSProperties,
    timelineItem: {
      position: 'relative' as const,
      paddingBottom: '20px',
      paddingLeft: '20px',
      borderLeft: '2px solid #e5e5e5'
    } as React.CSSProperties,
    timelineDot: {
      position: 'absolute' as const,
      left: '-9px',
      top: '0',
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      border: '2px solid #fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    } as React.CSSProperties,
    timelineContent: {
      background: '#f8f9fa',
      borderRadius: '8px',
      padding: '12px'
    } as React.CSSProperties,
    badge: {
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '0.65rem',
      fontWeight: '600',
      textTransform: 'uppercase' as const
    } as React.CSSProperties,
    badgeVerified: { background: '#dcfce7', color: '#166534' },
    badgePending: { background: '#fef3c7', color: '#92400e' },
    badgeUrgent: { background: '#fee2e2', color: '#dc2626' },
    alertCard: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '14px',
      borderRadius: '10px',
      marginBottom: '12px'
    } as React.CSSProperties,
    alertWarning: { background: '#fef3c7', border: '1px solid #f59e0b' },
    alertDanger: { background: '#fee2e2', border: '1px solid #ef4444' },
    alertInfo: { background: '#dbeafe', border: '1px solid #3b82f6' },
    alertSuccess: { background: '#dcfce7', border: '1px solid #22c55e' },
    primaryButton: {
      background: 'linear-gradient(135deg, #D4AF37 0%, #f4cf67 50%, #D4AF37 100%)',
      border: 'none',
      color: '#000',
      padding: '12px 20px',
      fontSize: '0.85rem',
      fontWeight: '600',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      boxShadow: '0 2px 8px rgba(212, 175, 55, 0.3)'
    } as React.CSSProperties,
    secondaryButton: {
      background: '#fff',
      border: '2px solid #D4AF37',
      color: '#1a1a2e',
      padding: '10px 16px',
      fontSize: '0.8rem',
      fontWeight: '600',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    } as React.CSSProperties,
    outlineButton: {
      background: 'transparent',
      border: '1px solid #e5e5e5',
      color: '#666',
      padding: '8px 12px',
      fontSize: '0.75rem',
      fontWeight: '500',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    } as React.CSSProperties,
    progressBar: {
      height: '8px',
      background: '#e5e5e5',
      borderRadius: '4px',
      overflow: 'hidden',
      marginBottom: '8px'
    } as React.CSSProperties,
    progressFill: {
      height: '100%',
      borderRadius: '4px',
      transition: 'width 0.5s ease'
    } as React.CSSProperties,
    receiptItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px',
      borderBottom: '1px solid #f0f0f0'
    } as React.CSSProperties,
    modalOverlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    } as React.CSSProperties,
    modalContent: {
      background: '#fff',
      borderRadius: '16px',
      width: '100%',
      maxWidth: '400px',
      overflow: 'hidden'
    } as React.CSSProperties,
    modalHeader: {
      background: '#1a1a2e',
      color: '#fff',
      padding: '16px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    } as React.CSSProperties,
    modalBody: {
      padding: '20px'
    } as React.CSSProperties,
    uploadZone: {
      border: '2px dashed #D4AF37',
      borderRadius: '12px',
      padding: '30px',
      textAlign: 'center' as const,
      cursor: 'pointer',
      transition: 'all 0.2s'
    } as React.CSSProperties,
    input: {
      width: '100%',
      padding: '12px',
      border: '1px solid #e5e5e5',
      borderRadius: '8px',
      fontSize: '0.9rem',
      marginBottom: '12px',
      boxSizing: 'border-box' as const
    } as React.CSSProperties,
    select: {
      width: '100%',
      padding: '12px',
      border: '1px solid #e5e5e5',
      borderRadius: '8px',
      fontSize: '0.9rem',
      marginBottom: '12px',
      background: '#fff',
      boxSizing: 'border-box' as const
    } as React.CSSProperties,
    emptyState: {
      textAlign: 'center' as const,
      padding: '40px 20px'
    } as React.CSSProperties,
  };

  // ============================================
  // LOADING STATE
  // ============================================
  
  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}><Building2 size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />{t('corporateServicesUae')}</h1>
        </div>
        <div style={styles.emptyState}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '3px solid #e5e5e5',
            borderTopColor: '#D4AF37',
            borderRadius: '50%',
            margin: '0 auto 16px',
            animation: 'spin 1s linear infinite'
          }} />
          <p>{t('loading')}</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // ============================================
  // NO COMPANY STATE - SETUP FORM
  // ============================================
  
  // Only show setup if no company exists AND showCompanySetup is true
  // Once company is created, this should show the main dashboard
  if (showCompanySetup && !company) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}><Building2 size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />{t('corporateServicesUae')}</h1>
          <p style={styles.subtitle}>{t('setupYourCompany')}</p>
        </div>
        
        <div style={{ ...styles.section, padding: '20px' }}>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '1.1rem', fontWeight: '600' }}>
            {t('companySetup')}
          </h2>
          
          <div>
            <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#666', display: 'block', marginBottom: '6px' }}>
              {t('companyName')} *
            </label>
            <input
              type="text"
              style={styles.input}
              placeholder="e.g. My Company FZCO"
              value={newCompany.companyName}
              onChange={e => setNewCompany({ ...newCompany, companyName: e.target.value })}
            />
          </div>
          
          <div>
            <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#666', display: 'block', marginBottom: '6px' }}>
              {t('licenseNumber')} *
            </label>
            <input
              type="text"
              style={styles.input}
              placeholder="e.g. DMCC-12345"
              value={newCompany.licenseNumber}
              onChange={e => setNewCompany({ ...newCompany, licenseNumber: e.target.value })}
            />
          </div>
          
          <div>
            <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#666', display: 'block', marginBottom: '6px' }}>
              {t('licenseExpiry')}
            </label>
            <input
              type="date"
              style={styles.input}
              value={newCompany.licenseExpiry}
              onChange={e => setNewCompany({ ...newCompany, licenseExpiry: e.target.value })}
            />
          </div>
          
          <div>
            <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#666', display: 'block', marginBottom: '6px' }}>
              {t('establishmentDate')}
            </label>
            <input
              type="date"
              style={styles.input}
              value={newCompany.establishmentDate}
              onChange={e => setNewCompany({ ...newCompany, establishmentDate: e.target.value })}
            />
          </div>
          
          <div>
            <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#666', display: 'block', marginBottom: '6px' }}>
              {t('freeZone')}
            </label>
            <select
              style={styles.select}
              value={newCompany.freeZone}
              onChange={e => setNewCompany({ ...newCompany, freeZone: e.target.value })}
            >
              <option value="DMCC">DMCC</option>
              <option value="DIFC">DIFC</option>
              <option value="JAFZA">JAFZA</option>
              <option value="DAFZA">DAFZA</option>
              <option value="IFZA">IFZA</option>
              <option value="RAKEZ">RAKEZ</option>
              <option value="Mainland">Mainland</option>
              <option value="Other">Other</option>
            </select>
          </div>
          
          <button 
            style={{ ...styles.primaryButton, width: '100%', justifyContent: 'center', marginTop: '8px' }}
            onClick={handleCreateCompany}
          >
            <Building2 size={18} /> {t('createCompany')}
          </button>
          
          <p style={{ fontSize: '0.75rem', color: '#888', textAlign: 'center', marginTop: '16px' }}>
            {t('companySetupNote')}
          </p>
        </div>
      </div>
    );
  }

  // If no company and not in setup mode, show prompt to create
  if (!company) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}><Building2 size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />{t('corporateServicesUae')}</h1>
        </div>
        <div style={styles.emptyState}>
          <Building2 size={48} color="#D4AF37" style={{ margin: '0 auto 16px' }} />
          <h3 style={{ margin: '0 0 8px 0', color: '#1a1a2e' }}>{t('noCompanyYet')}</h3>
          <p style={{ color: '#666', marginBottom: '20px' }}>{t('setupYourCompanyToGetStarted')}</p>
          <button 
            style={styles.primaryButton}
            onClick={() => setShowCompanySetup(true)}
          >
            <Plus size={18} /> {t('createCompany')}
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // MAIN RENDER
  // ============================================
  
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}><Building2 size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />{t('corporateServicesUae')}</h1>
        <p style={styles.subtitle}>{currentInvestor?.name} - {t('companyManagement')}</p>
      </div>

      {/* Company Overview Card */}
      <div style={styles.overviewCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={styles.companyName}>{company.companyName}</h2>
            <p style={styles.licenseNumber}>{t('licenseNo')}: {company.licenseNumber}</p>
          </div>
          <span style={{ ...styles.badge, background: '#D4AF37', color: '#000' }}>
            {company.freeZone || 'FZCO'}
          </span>
        </div>
        
        <div style={styles.statsGrid}>
          <div style={styles.statItem}>
            <div style={styles.statLabel}>{t('formationProgress')}</div>
            <div style={styles.statValue}>{verifiedDocsCount}/{FORMATION_STEPS.filter(s => s.required).length}</div>
          </div>
          <div style={styles.statItem}>
            <div style={styles.statLabel}>{t('licenseExpiry')}</div>
            <div style={{ ...styles.statValue, color: daysUntilExpiry < 60 ? '#ef4444' : '#22c55e' }}>
              {daysUntilExpiry > 0 ? `${daysUntilExpiry} ${t('days')}` : t('expired')}
            </div>
          </div>
          <div style={styles.statItem}>
            <div style={styles.statLabel}>{t('vatStatus')}</div>
            <div style={styles.statValue}>{company.vatRegistered ? t('registered') : t('notRequired')}</div>
          </div>
          <div style={styles.statItem}>
            <div style={styles.statLabel}>{t('ctStatus')}</div>
            <div style={{ ...styles.statValue, color: company.ctRegistrationRequired ? '#f59e0b' : '#22c55e' }}>
              {company.ctRegistrationRequired ? t('actionRequired') : t('compliant')}
            </div>
          </div>
        </div>
      </div>

      {/* 1. Company Formation - Document Hub */}
      <div style={styles.section}>
        <div style={styles.sectionHeader} onClick={() => toggleSection('formation')}>
          <div style={styles.sectionTitle}>
            <div style={{ ...styles.sectionIcon, background: '#dbeafe' }}>
              <Building2 size={18} color="#3b82f6" />
            </div>
            <div>
              <span>{t('companyFormation')}</span>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.7rem', color: '#888', fontWeight: '400' }}>
                {t('documentHub')}
              </p>
            </div>
          </div>
          {expandedSections.has('formation') ? <ChevronUp size={20} color="#888" /> : <ChevronDown size={20} color="#888" />}
        </div>
        
        {expandedSections.has('formation') && (
          <div style={styles.sectionContent}>
            {/* Progress bar */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.75rem', color: '#666' }}>{t('overallProgress')}</span>
                <span style={{ fontSize: '0.85rem', fontWeight: '600' }}>
                  {verifiedDocsCount}/{FORMATION_STEPS.filter(s => s.required).length}
                </span>
              </div>
              <div style={styles.progressBar}>
                <div style={{ 
                  ...styles.progressFill, 
                  width: `${(verifiedDocsCount / FORMATION_STEPS.filter(s => s.required).length) * 100}%`,
                  background: 'linear-gradient(90deg, #D4AF37, #22c55e)'
                }} />
              </div>
            </div>
            
            {/* Timeline */}
            <div style={styles.timeline}>
              {FORMATION_STEPS.map((step, index) => {
                const document = getDocumentByType(step.id);
                const isComplete = document?.status === 'verified';
                const isPending = document?.status === 'pending';
                
                return (
                  <div 
                    key={step.id}
                    style={{
                      ...styles.timelineItem,
                      borderLeftColor: isComplete ? '#22c55e' : isPending ? '#D4AF37' : '#e5e5e5'
                    }}
                  >
                    <div style={{
                      ...styles.timelineDot,
                      background: isComplete ? '#22c55e' : isPending ? '#D4AF37' : '#e5e5e5'
                    }}>
                      {isComplete && <Check size={10} color="#fff" />}
                    </div>
                    <div style={styles.timelineContent}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <h4 style={{ margin: '0 0 4px 0', fontSize: '0.85rem', fontWeight: '600' }}>
                            {t(step.key)}
                          </h4>
                          <p style={{ margin: 0, fontSize: '0.7rem', color: '#888' }}>
                            {step.required ? t('required') : t('optional')}
                          </p>
                        </div>
                        {document ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ ...styles.badge, ...(isComplete ? styles.badgeVerified : styles.badgePending) }}>
                              {isComplete ? t('verified') : t('pending')}
                            </span>
                            <button 
                              onClick={() => handleDeleteDocument(document.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                            >
                              <Trash2 size={14} color="#ef4444" />
                            </button>
                          </div>
                        ) : (
                          <button style={styles.outlineButton} onClick={() => handleUpload(step.id)}>
                            <Upload size={12} /> {t('upload')}
                          </button>
                        )}
                      </div>
                      {document && (
                        <div style={{ fontSize: '0.75rem', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <FileText size={12} /> {document.fileName}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* 2. Deadlines & Compliance */}
      <div style={styles.section}>
        <div style={styles.sectionHeader} onClick={() => toggleSection('compliance')}>
          <div style={styles.sectionTitle}>
            <div style={{ ...styles.sectionIcon, background: '#fef3c7' }}>
              <Shield size={18} color="#f59e0b" />
            </div>
            <div>
              <span>{t('deadlinesCompliance')}</span>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.7rem', color: '#888', fontWeight: '400' }}>
                {t('ctAndFilings')}
              </p>
            </div>
          </div>
          {company.ctRegistrationRequired && (
            <span style={{ ...styles.badge, ...styles.badgeUrgent }}>{t('actionNeeded')}</span>
          )}
        </div>
        
        {expandedSections.has('compliance') && (
          <div style={styles.sectionContent}>
            {company.ctRegistrationRequired && (
              <div style={{ ...styles.alertCard, ...styles.alertWarning }}>
                <AlertCircle size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '0.9rem', fontWeight: '600', color: '#92400e' }}>
                    {t('ctRegistrationRequired')}
                  </h4>
                  <p style={{ margin: '0 0 12px 0', fontSize: '0.8rem', color: '#a16207' }}>
                    {t('ctRegistrationDeadline')}: <strong>{company.ctRegistrationDeadline ? formatDate(company.ctRegistrationDeadline) : t('checkWithAdvisor')}</strong>
                  </p>
                  <button style={styles.primaryButton} onClick={openCalendly}>
                    <Calendar size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{t('bookAppointment')}
                  </button>
                </div>
              </div>
            )}

            <div style={{ ...styles.alertCard, ...styles.alertInfo }}>
              <Calendar size={20} color="#3b82f6" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '0.9rem', fontWeight: '600', color: '#1e40af' }}>
                  {t('ctFilingDue')}
                </h4>
                <p style={{ margin: '0 0 12px 0', fontSize: '0.8rem', color: '#1d4ed8' }}>
                  {t('nextFilingDeadline')}: <strong>{company.ctFilingDue ? formatDate(company.ctFilingDue) : t('toBeConfirmed')}</strong>
                </p>
                <button style={styles.secondaryButton} onClick={openCalendly}>
                  <Calendar size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{t('bookAppointment')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 3. Accounting */}
      <div style={styles.section}>
        <div style={styles.sectionHeader} onClick={() => toggleSection('accounting')}>
          <div style={styles.sectionTitle}>
            <div style={{ ...styles.sectionIcon, background: '#dcfce7' }}>
              <Receipt size={18} color="#22c55e" />
            </div>
            <div>
              <span>{t('accounting')}</span>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.7rem', color: '#888', fontWeight: '400' }}>
                {t('receiptsAndExpenses')}
              </p>
            </div>
          </div>
          <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#1a1a2e' }}>
            {receipts.length} {t('receipts')}
          </span>
        </div>
        
        {expandedSections.has('accounting') && (
          <div style={styles.sectionContent}>
            <button 
              style={{ ...styles.primaryButton, width: '100%', justifyContent: 'center', marginBottom: '16px' }}
              onClick={() => setShowReceiptModal(true)}
            >
              <Plus size={16} /> {t('uploadReceipt')}
            </button>
            
            {receipts.length > 0 ? (
              <>
                <div style={{ borderRadius: '10px', border: '1px solid #e5e5e5', overflow: 'hidden' }}>
                  {receipts.map((receipt, index) => (
                    <div 
                      key={receipt.id}
                      style={{ 
                        ...styles.receiptItem,
                        borderBottom: index === receipts.length - 1 ? 'none' : '1px solid #f0f0f0'
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1a1a2e' }}>
                          {receipt.description}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#888' }}>
                          {formatDate(receipt.date)} • {receipt.category}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1a1a2e' }}>
                          {formatAED(receipt.amount)}
                        </span>
                        <button 
                          onClick={() => handleDeleteReceipt(receipt.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                        >
                          <Trash2 size={14} color="#ef4444" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div style={{ marginTop: '16px', padding: '12px', background: '#f8f9fa', borderRadius: '8px', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.85rem', color: '#666' }}>{t('totalExpenses')}</span>
                  <span style={{ fontSize: '1rem', fontWeight: '700', color: '#1a1a2e' }}>
                    {formatAED(totalExpenses)}
                  </span>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                <Receipt size={32} color="#ccc" style={{ margin: '0 auto 8px' }} />
                <p style={{ margin: 0, fontSize: '0.85rem' }}>{t('noReceiptsYet')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 4. License Management */}
      <div style={styles.section}>
        <div style={styles.sectionHeader} onClick={() => toggleSection('license')}>
          <div style={styles.sectionTitle}>
            <div style={{ ...styles.sectionIcon, background: daysUntilExpiry < 60 ? '#fee2e2' : '#f3e8ff' }}>
              <FileText size={18} color={daysUntilExpiry < 60 ? '#ef4444' : '#9333ea'} />
            </div>
            <div>
              <span>{t('licenseManagement')}</span>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.7rem', color: '#888', fontWeight: '400' }}>
                {t('renewalAndCompliance')}
              </p>
            </div>
          </div>
          {daysUntilExpiry < 60 && daysUntilExpiry > 0 && (
            <span style={{ ...styles.badge, ...styles.badgeUrgent }}>
              {daysUntilExpiry} {t('daysLeft')}
            </span>
          )}
        </div>
        
        {expandedSections.has('license') && (
          <div style={styles.sectionContent}>
            {daysUntilExpiry < 60 ? (
              <div style={{ ...styles.alertCard, ...styles.alertDanger }}>
                <Bell size={20} color="#ef4444" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '0.9rem', fontWeight: '600', color: '#dc2626' }}>
                    {daysUntilExpiry > 0 ? t('licenseExpiryWarning') : t('expired')}
                  </h4>
                  <p style={{ margin: '0 0 12px 0', fontSize: '0.8rem', color: '#b91c1c' }}>
                    {t('licenseExpiresOn')}: <strong>{formatDate(company.licenseExpiry)}</strong>
                  </p>
                  <button style={styles.primaryButton} onClick={openCalendly}>
                    <RefreshCw size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{t('renewLicense')}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ ...styles.alertCard, ...styles.alertSuccess }}>
                <Check size={20} color="#22c55e" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '0.9rem', fontWeight: '600', color: '#166534' }}>
                    {t('licenseValid')}
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#15803d' }}>
                    {t('expiresOn')}: {formatDate(company.licenseExpiry)} ({daysUntilExpiry} {t('daysRemaining')})
                  </p>
                </div>
              </div>
            )}

            <div style={{ marginTop: '12px', padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Bell size={16} color="#888" />
                <span style={{ fontSize: '0.8rem', color: '#666' }}>{t('pushNotificationInfo')}</span>
              </div>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#888' }}>
                {t('licenseReminderNote')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* 5. VAT Management */}
      <div style={styles.section}>
        <div style={styles.sectionHeader} onClick={() => toggleSection('vat')}>
          <div style={styles.sectionTitle}>
            <div style={{ ...styles.sectionIcon, background: '#fce7f3' }}>
              <CreditCard size={18} color="#db2777" />
            </div>
            <div>
              <span>{t('vatManagement')}</span>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.7rem', color: '#888', fontWeight: '400' }}>
                {t('valueAddedTax')}
              </p>
            </div>
          </div>
          <span style={{ ...styles.badge, ...(vatProgress >= 100 ? styles.badgeUrgent : styles.badgeVerified) }}>
            {vatProgress >= 100 ? t('registrationRequired') : t('belowThreshold')}
          </span>
        </div>
        
        {expandedSections.has('vat') && (
          <div style={styles.sectionContent}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.8rem', color: '#666' }}>{t('revenueProgress')}</span>
                <span style={{ fontSize: '0.85rem', fontWeight: '600', color: vatProgress >= 100 ? '#ef4444' : '#1a1a2e' }}>
                  {formatAED(company.currentRevenue || 0)} / {formatAED(VAT_THRESHOLD)}
                </span>
              </div>
              <div style={styles.progressBar}>
                <div style={{ 
                  ...styles.progressFill, 
                  width: `${vatProgress}%`,
                  background: vatProgress >= 100 ? '#ef4444' : vatProgress >= 80 ? '#f59e0b' : '#22c55e'
                }} />
              </div>
              <p style={{ fontSize: '0.75rem', color: '#888', margin: '8px 0 0 0' }}>
                {t('vatThresholdNote')}
              </p>
            </div>

            {vatProgress >= 100 ? (
              <div style={{ ...styles.alertCard, ...styles.alertDanger }}>
                <AlertCircle size={20} color="#ef4444" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '0.9rem', fontWeight: '600', color: '#dc2626' }}>
                    {t('vatRegistrationRequired')}
                  </h4>
                  <p style={{ margin: '0 0 12px 0', fontSize: '0.8rem', color: '#b91c1c' }}>
                    {t('vatThresholdExceeded')}
                  </p>
                  <button style={styles.primaryButton} onClick={openCalendly}>
                    <ClipboardList size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{t('registerForVat')}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ ...styles.alertCard, ...styles.alertSuccess }}>
                <Check size={20} color="#22c55e" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '0.9rem', fontWeight: '600', color: '#166534' }}>
                    {t('vatNotRequired')}
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#15803d' }}>
                    {t('vatBelowThreshold')} ({formatAED(VAT_THRESHOLD - (company.currentRevenue || 0))} {t('remaining')})
                  </p>
                </div>
              </div>
            )}

            <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button style={styles.secondaryButton} onClick={() => handleUpload('vat_receipt')}>
                <Upload size={14} /> {t('uploadVatReceipt')}
              </button>
              <button style={styles.outlineButton} onClick={() => handleUpload('vat_filing')}>
                <FileText size={14} /> {t('uploadVatFiling')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions Card */}
      <div style={{ 
        background: 'linear-gradient(135deg, #D4AF37 0%, #f4cf67 100%)', 
        borderRadius: '12px', 
        padding: '16px',
        marginTop: '8px'
      }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: '700', color: '#000' }}>
          {t('needHelp')}
        </h3>
        <button 
          style={{ 
            ...styles.primaryButton, 
            background: '#1a1a2e', 
            color: '#fff',
            width: '100%',
            justifyContent: 'center'
          }}
          onClick={openCalendly}
        >
          <Calendar size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{t('bookConsultation')}
        </button>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div style={styles.modalOverlay} onClick={() => setShowUploadModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>
                {t('uploadDocument')}
              </h3>
              <button 
                onClick={() => setShowUploadModal(false)}
                style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={styles.modalBody}>
              <label style={styles.uploadZone}>
                <input 
                  type="file" 
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                {isUploading ? (
                  <div>
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      border: '3px solid #e5e5e5',
                      borderTopColor: '#D4AF37',
                      borderRadius: '50%',
                      margin: '0 auto 12px',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <p style={{ margin: 0, color: '#666' }}>{t('uploading')}</p>
                  </div>
                ) : (
                  <>
                    <Upload size={40} color="#D4AF37" style={{ marginBottom: '12px' }} />
                    <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#1a1a2e' }}>
                      {t('clickToUpload')}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>
                      PDF, JPG, PNG (max 10MB)
                    </p>
                  </>
                )}
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && (
        <div style={styles.modalOverlay} onClick={() => setShowReceiptModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>
                {t('uploadReceipt')}
              </h3>
              <button 
                onClick={() => setShowReceiptModal(false)}
                style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={styles.modalBody}>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#666', display: 'block', marginBottom: '6px' }}>
                  {t('description')} *
                </label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="e.g. Office supplies"
                  value={newReceipt.description}
                  onChange={e => setNewReceipt({ ...newReceipt, description: e.target.value })}
                />
              </div>
              
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#666', display: 'block', marginBottom: '6px' }}>
                  {t('amount')} (AED) *
                </label>
                <input
                  type="number"
                  style={styles.input}
                  placeholder="0.00"
                  value={newReceipt.amount}
                  onChange={e => setNewReceipt({ ...newReceipt, amount: e.target.value })}
                />
              </div>
              
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#666', display: 'block', marginBottom: '6px' }}>
                  {t('category')}
                </label>
                <select
                  style={styles.select}
                  value={newReceipt.category}
                  onChange={e => setNewReceipt({ ...newReceipt, category: e.target.value })}
                >
                  <option value="operations">Operations</option>
                  <option value="software">Software</option>
                  <option value="marketing">Marketing</option>
                  <option value="travel">Travel</option>
                  <option value="utilities">Utilities</option>
                  <option value="rent">Rent</option>
                  <option value="salary">Salary</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#666', display: 'block', marginBottom: '6px' }}>
                  {t('date')}
                </label>
                <input
                  type="date"
                  style={styles.input}
                  value={newReceipt.date}
                  onChange={e => setNewReceipt({ ...newReceipt, date: e.target.value })}
                />
              </div>
              
              <button 
                style={{ ...styles.primaryButton, width: '100%', justifyContent: 'center', marginTop: '8px' }}
                onClick={handleAddReceipt}
              >
                <Plus size={16} /> {t('addReceipt')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporateServicesView;